import type { ComponentType, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { BiCheckCircle, BiListCheck, BiUserPlus } from 'react-icons/bi';
import type {
  ExamApprovalDecidedPayload,
  ExamApprovalRequestedPayload,
  LeadAssignedPayload,
  NotificationItem,
  NotificationType,
} from '@/types/shared';

/**
 * Client-side composition of the notification "sentence" from `type` + structured `payload`
 * (DEC-NOT-M1 / D4 — the server never ships a rendered string, only `type` + `payload`, so AR/EN +
 * a live language toggle both work off the same row). This pass wires exactly one type,
 * `lead_assigned` — every map/switch below is written per-type so a future type is additive (new
 * icon-map row + new i18n message key(s) + one new switch case), per `16-notifications-ux.md` §3.2.
 */

/** Icon + tint-chip tone per notification `type` — `16-notifications-ux.md` §3.1 table. */
const NOTIFICATION_ICON: Record<NotificationType, { Icon: ComponentType; tone: 'info' }> = {
  lead_assigned: { Icon: BiUserPlus, tone: 'info' },
  exam_approval_requested: { Icon: BiListCheck, tone: 'info' },
  exam_approval_decided: { Icon: BiCheckCircle, tone: 'info' },
};

export function getNotificationIcon(type: NotificationType): { Icon: ComponentType; tone: string } {
  return NOTIFICATION_ICON[type];
}

/** Deep-link target for a notification's `relatedEntity` — currently only `lead` -> `/leads/:id`. */
export function getNotificationLink(notification: NotificationItem): string | null {
  const related = notification.relatedEntity;
  if (!related) return null;
  switch (related.entity) {
    case 'lead':
      return `/leads/${related.id}`;
    case 'exam_booking':
      // `related.id` is the studentCode — deep-link to that student's exam sections.
      return `/exam-bookings/${related.id}`;
    default:
      return null;
  }
}

/** Sentinel tokens swapped into the i18n template so the rendered version can split the
 * (already-translated) string back apart and wrap only the name substrings in `<bdi>` — same
 * technique as `ActivityFeedWidget.renderEntityLabel` (T94). */
const LEAD_NAME_TOKEN = '@@FA_NOTIF_LEAD_NAME@@';
const ASSIGNED_BY_TOKEN = '@@FA_NOTIF_ASSIGNED_BY@@';

/** Plain-text composed message — used for the aria-live arrival announcement and the optional
 * toast-on-arrival body (§4.4/§6), where no `<bdi>` structure is needed. */
export function composeNotificationText(t: TFunction, notification: NotificationItem): string {
  switch (notification.type) {
    case 'lead_assigned': {
      const { leadName, assignedByName } = notification.payload as LeadAssignedPayload;
      return assignedByName
        ? t('notifications.messages.leadAssignedBy', { assignedByName, leadName })
        : t('notifications.messages.leadAssigned', { leadName });
    }
    case 'exam_approval_requested': {
      const { studentName, examSection, requestedByName } =
        notification.payload as ExamApprovalRequestedPayload;
      return t('notifications.messages.examApprovalRequested', {
        studentName,
        examSection,
        requestedByName,
      });
    }
    case 'exam_approval_decided': {
      const { studentName, examSection, decision, reason } =
        notification.payload as ExamApprovalDecidedPayload;
      if (decision === 'rejected') {
        return reason
          ? t('notifications.messages.examRejectedWithReason', { studentName, examSection, reason })
          : t('notifications.messages.examRejected', { studentName, examSection });
      }
      return t('notifications.messages.examApproved', { studentName, examSection });
    }
    default:
      return '';
  }
}

/** JSX-rendered composed message with the lead name (and assigner name, if present) individually
 * `<bdi>`-wrapped — `16-notifications-ux.md` §3.2/§6 "bidi safety". */
export function renderNotificationMessage(t: TFunction, notification: NotificationItem): ReactNode {
  switch (notification.type) {
    case 'lead_assigned': {
      const { leadName, assignedByName } = notification.payload as LeadAssignedPayload;
      const hasAssignedBy = Boolean(assignedByName);
      const key = hasAssignedBy
        ? 'notifications.messages.leadAssignedBy'
        : 'notifications.messages.leadAssigned';
      const raw = t(key, {
        leadName: LEAD_NAME_TOKEN,
        assignedByName: hasAssignedBy ? ASSIGNED_BY_TOKEN : undefined,
        interpolation: { escapeValue: false },
      });
      const segments = raw.split(new RegExp(`(${LEAD_NAME_TOKEN}|${ASSIGNED_BY_TOKEN})`, 'g'));

      return segments
        .filter((segment) => segment.length > 0)
        .map((segment, idx) => {
          if (segment === LEAD_NAME_TOKEN) {
            return <bdi key={`lead-${idx}`}>{leadName}</bdi>;
          }
          if (segment === ASSIGNED_BY_TOKEN) {
            return <bdi key={`by-${idx}`}>{assignedByName}</bdi>;
          }
          return <span key={`text-${idx}`}>{segment}</span>;
        });
    }
    // Exam approval messages embed the studentName via i18n interpolation; a plain span is enough
    // (i18next isolates interpolated values, and the student name here is not free bidi text).
    case 'exam_approval_requested':
    case 'exam_approval_decided':
      return <span>{composeNotificationText(t, notification)}</span>;
    default:
      return null;
  }
}
